import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, timer } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface VirtualScrollItem {
  index: number;
  data: any;
  height: number;
  visible: boolean;
}

export interface VirtualScrollConfig {
  itemHeight: number;
  bufferSize: number;
  threshold: number;
  smoothScrolling: boolean;
  autoResize: boolean;
  trackBy?: (index: number, item: any) => any;
}

@Component({
  selector: 'app-virtual-scroll',
  templateUrl: './virtual-scroll.component.html',
  styleUrls: ['./virtual-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualScrollComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() items: any[] = [];
  @Input() itemHeight: number = 50;
  @Input() bufferSize: number = 5;
  @Input() threshold: number = 100;
  @Input() smoothScrolling: boolean = true;
  @Input() autoResize: boolean = true;
  @Input() trackBy?: (index: number, item: any) => any;
  @Input() loading: boolean = false;

  @Output() itemClick = new EventEmitter<any>();
  @Output() itemSelect = new EventEmitter<any>();
  @Output() scrollEnd = new EventEmitter<void>();

  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef;
  @ViewChild('contentContainer', { static: true }) contentContainer!: ElementRef;

  private config: VirtualScrollConfig = {
    itemHeight: 50,
    bufferSize: 5,
    threshold: 100,
    smoothScrolling: true,
    autoResize: true
  };

  private visibleItemsSubject = new BehaviorSubject<VirtualScrollItem[]>([]);
  private scrollTop = 0;
  private containerHeight = 0;
  private totalHeight = 0;
  private startIndex = 0;
  private endIndex = 0;
  private visibleCount = 0;

  // Observables
  visibleItems$ = this.visibleItemsSubject.asObservable();

  constructor() {}

  ngOnInit(): void {
    this.updateConfig();
    this.calculateDimensions();
    this.updateVisibleItems();
  }

  ngAfterViewInit(): void {
    this.setupScrollListener();
    this.setupResizeListener();
  }

  ngOnDestroy(): void {
    // Cleanup será feito automaticamente pelos observables
  }

  /**
   * Atualizar configuração
   */
  private updateConfig(): void {
    this.config = {
      itemHeight: this.itemHeight,
      bufferSize: this.bufferSize,
      threshold: this.threshold,
      smoothScrolling: this.smoothScrolling,
      autoResize: this.autoResize,
      trackBy: this.trackBy
    };
  }

  /**
   * Calcular dimensões
   */
  private calculateDimensions(): void {
    this.containerHeight = this.scrollContainer.nativeElement.clientHeight;
    this.totalHeight = this.items.length * this.config.itemHeight;
    this.visibleCount = Math.ceil(this.containerHeight / this.config.itemHeight);
  }

  /**
   * Configurar listener de scroll
   */
  private setupScrollListener(): void {
    fromEvent(this.scrollContainer.nativeElement, 'scroll')
      .pipe(
        debounceTime(16), // ~60fps
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.handleScroll();
      });
  }

  /**
   * Configurar listener de resize
   */
  private setupResizeListener(): void {
    if (!this.config.autoResize) {
      return;
    }

    fromEvent(window, 'resize')
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.calculateDimensions();
        this.updateVisibleItems();
      });
  }

  /**
   * Manipular scroll
   */
  private handleScroll(): void {
    const element = this.scrollContainer.nativeElement;
    this.scrollTop = element.scrollTop;
    
    this.updateVisibleItems();
    this.checkScrollEnd();
  }

  /**
   * Atualizar itens visíveis
   */
  private updateVisibleItems(): void {
    if (this.items.length === 0) {
      this.visibleItemsSubject.next([]);
      return;
    }

    // Calcular índices visíveis
    this.startIndex = Math.max(0, Math.floor(this.scrollTop / this.config.itemHeight) - this.config.bufferSize);
    this.endIndex = Math.min(
      this.items.length - 1,
      Math.ceil((this.scrollTop + this.containerHeight) / this.config.itemHeight) + this.config.bufferSize
    );

    // Criar itens visíveis
    const visibleItems: VirtualScrollItem[] = [];
    
    for (let i = this.startIndex; i <= this.endIndex; i++) {
      if (i >= 0 && i < this.items.length) {
        visibleItems.push({
          index: i,
          data: this.items[i],
          height: this.config.itemHeight,
          visible: true
        });
      }
    }

    this.visibleItemsSubject.next(visibleItems);
    this.updateContentContainer();
  }

  /**
   * Atualizar container de conteúdo
   */
  private updateContentContainer(): void {
    const contentElement = this.contentContainer.nativeElement;
    
    // Altura total do conteúdo
    contentElement.style.height = `${this.totalHeight}px`;
    
    // Transform para posicionar itens visíveis
    const offsetY = this.startIndex * this.config.itemHeight;
    contentElement.style.transform = `translateY(${offsetY}px)`;
  }

  /**
   * Verificar fim do scroll
   */
  private checkScrollEnd(): void {
    const element = this.scrollContainer.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - this.config.threshold) {
      this.scrollEnd.emit();
    }
  }

  /**
   * Scroll para item específico
   */
  scrollToItem(index: number): void {
    if (index < 0 || index >= this.items.length) {
      return;
    }

    const scrollTop = index * this.config.itemHeight;
    this.scrollTo(scrollTop);
  }

  /**
   * Scroll para posição específica
   */
  scrollTo(scrollTop: number): void {
    const element = this.scrollContainer.nativeElement;
    
    if (this.config.smoothScrolling) {
      element.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    } else {
      element.scrollTop = scrollTop;
    }
  }

  /**
   * Scroll para o topo
   */
  scrollToTop(): void {
    this.scrollTo(0);
  }

  /**
   * Scroll para o fim
   */
  scrollToBottom(): void {
    this.scrollTo(this.totalHeight);
  }

  /**
   * Obter item por índice
   */
  getItemByIndex(index: number): any {
    return this.items[index];
  }

  /**
   * Obter índice do item visível
   */
  getVisibleItemIndex(item: any): number {
    return this.items.indexOf(item);
  }

  /**
   * Verificar se item está visível
   */
  isItemVisible(index: number): boolean {
    return index >= this.startIndex && index <= this.endIndex;
  }

  /**
   * Obter estatísticas do scroll
   */
  getScrollStats(): any {
    return {
      totalItems: this.items.length,
      visibleItems: this.endIndex - this.startIndex + 1,
      startIndex: this.startIndex,
      endIndex: this.endIndex,
      scrollTop: this.scrollTop,
      containerHeight: this.containerHeight,
      totalHeight: this.totalHeight,
      scrollPercentage: (this.scrollTop / (this.totalHeight - this.containerHeight)) * 100
    };
  }

  /**
   * Atualizar itens
   */
  updateItems(newItems: any[]): void {
    this.items = newItems;
    this.calculateDimensions();
    this.updateVisibleItems();
  }

  /**
   * Adicionar item
   */
  addItem(item: any, index?: number): void {
    if (index !== undefined) {
      this.items.splice(index, 0, item);
    } else {
      this.items.push(item);
    }
    
    this.calculateDimensions();
    this.updateVisibleItems();
  }

  /**
   * Remover item
   */
  removeItem(index: number): void {
    if (index >= 0 && index < this.items.length) {
      this.items.splice(index, 1);
      this.calculateDimensions();
      this.updateVisibleItems();
    }
  }

  /**
   * Limpar itens
   */
  clearItems(): void {
    this.items = [];
    this.visibleItemsSubject.next([]);
    this.calculateDimensions();
  }

  /**
   * Obter trackBy function
   */
  getTrackByFn(): (index: number, item: VirtualScrollItem) => any {
    if (this.config.trackBy) {
      return (index: number, item: VirtualScrollItem) => this.config.trackBy!(item.index, item.data);
    }
    return (index: number, item: VirtualScrollItem) => item.index;
  }

  /**
   * Manipular clique no item
   */
  onItemClick(item: VirtualScrollItem): void {
    this.itemClick.emit(item.data);
  }

  /**
   * Manipular seleção do item
   */
  onItemSelect(item: VirtualScrollItem): void {
    this.itemSelect.emit(item.data);
  }

  /**
   * Obter estilo do item
   */
  getItemStyle(item: VirtualScrollItem): any {
    return {
      height: `${item.height}px`,
      position: 'absolute',
      top: `${item.index * this.config.itemHeight}px`,
      left: '0',
      right: '0'
    };
  }

  /**
   * Obter classe CSS do item
   */
  getItemClass(item: VirtualScrollItem): string {
    return `virtual-scroll-item virtual-scroll-item-${item.index}`;
  }
}
